'use client'

import { RadarLockup } from '@/components/brand/RadarMark'
import { VERSION } from '@/lib/version'
import { InstrumentLabel } from '../ui'
import type { NavItem, NavSection, RadarTab } from './nav-model'

/**
 * The 216px navigation rail.
 *
 * Rendered on desktop as a persistent aside and on mobile inside the drawer
 * (see DashboardShell) — one component, two placements, so the destinations
 * cannot diverge.
 *
 * The rail foot carries the app's colophon — attribution, version, site links
 * and the keyboard hints. It moved here from a full-width bar under the
 * scroll container on 2026-07-26, which also retired the prospect-score meter,
 * the "Watching" summary and the Chrome-extension link that used to sit here.
 */
export function DashboardSidebar({
  sections,
  activeTab,
  onNavigate,
}: {
  sections: NavSection[]
  activeTab: RadarTab
  onNavigate: (tab: RadarTab) => void
}) {
  return (
    <div className='flex h-full min-h-0 w-full flex-col bg-surface-app'>
      <div className='flex flex-col gap-1 border-b border-hairline px-5 pb-5 pt-6'>
        <RadarLockup />
      </div>

      <nav
        aria-label='Dashboard sections'
        className='flex flex-1 flex-col gap-0.5 overflow-y-auto px-3 py-4'
      >
        {sections.map((section, i) => (
          <div
            key={section.label ?? `primary-${i}`}
            className='flex flex-col gap-0.5'
          >
            {section.label && (
              <InstrumentLabel tone='muted' className='px-3 pb-1.5 pt-4'>
                {section.label}
              </InstrumentLabel>
            )}
            {section.items.map(item => (
              <SidebarLink
                key={item.tab}
                item={item}
                active={activeTab === item.tab}
                onSelect={onNavigate}
              />
            ))}
          </div>
        ))}
      </nav>

      {/* Colophon. Stacked rather than the two-column split the full-width bar
          used — 216px cannot carry attribution and links on one line. */}
      <div className='mt-auto flex flex-col gap-2.5 border-t border-hairline px-5 py-4'>
        <div className='flex flex-col gap-1'>
          <InstrumentLabel tone='muted'>
            Powered by Vector + Hive
          </InstrumentLabel>
          <span className='font-mono text-[11px] text-instrument'>
            {VERSION.full}
          </span>
        </div>
        <div className='flex items-center gap-3'>
          <a
            href='https://www.pixelmojo.io'
            target='_blank'
            rel='noopener noreferrer'
            className='text-[11px] text-muted-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-app'
          >
            pixelmojo.io
          </a>
          <a
            href='https://www.pixelmojo.io/contact-us'
            target='_blank'
            rel='noopener noreferrer'
            className='text-[11px] text-muted-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-app'
          >
            Contact
          </a>
        </div>
      </div>
    </div>
  )
}

function SidebarLink({
  item,
  active,
  onSelect,
}: {
  item: NavItem
  active: boolean
  onSelect: (tab: RadarTab) => void
}) {
  const Icon = item.icon
  const showBadge = !!item.badge && item.badge > 0
  const badgeColor =
    item.badgeTone === 'action' ? 'var(--cta)' : 'var(--pm-text-muted)'

  return (
    <button
      type='button'
      onClick={() => onSelect(item.tab)}
      aria-current={active ? 'page' : undefined}
      className={`flex items-center gap-2.5 border px-3 py-2.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset ${
        active
          ? 'border-hairline bg-surface-card font-medium text-foreground'
          : 'border-transparent text-muted-foreground hover:bg-surface-card hover:text-foreground'
      }`}
    >
      {/* 1.8, not lucide's default 2. At 16px a 2px stroke reads as a heavy
          blob; the radar-v2 mock draws its whole nav at 1.8, and that lighter
          weight is most of why those icons look sharper. */}
      <Icon
        size={16}
        strokeWidth={1.8}
        aria-hidden='true'
        className='shrink-0'
        style={active ? { color: 'var(--primary)' } : undefined}
      />
      <span className='flex-1 truncate text-left'>{item.label}</span>
      {showBadge && (
        <span
          className='shrink-0 border px-1.5 py-px font-mono text-[11px] leading-none tabular-nums'
          style={{
            color: badgeColor,
            borderColor: `color-mix(in srgb, ${badgeColor} 40%, transparent)`,
          }}
        >
          {item.badge}
        </span>
      )}
    </button>
  )
}
